/**
 * Módulo 3 — Contrato Zod (molde de salida LLM).
 * Valida la forma raíz de exámenes y guías generados por Mistral.
 */

import { z } from 'zod';
import { CourseProfileSchema } from './courseProfileSchema';
import {
  sanitizeLlmExamRaw,
  type LlmExamSanitizeContext,
} from './llmExamSemanticSanitizer';

export type { LlmExamSanitizeContext };
export {
  sanitizeGeometryBlock,
  sanitizeIntrusiveModeBlock,
  sanitizeLlmExamRaw,
} from './llmExamSemanticSanitizer';

const GameModeSchema = z.enum([
  'arena',
  'completar',
  'order',
  'vincula',
  'desarrollo',
  'agrupa',
  'dictado',
  'anatomia',
  'rhythm_canvas',
  'melody_staff',
  'ear_training',
  'symbol_analysis',
  'geometry_canvas',
  'cartesian_plane',
  'math_equation',
  'number_line',
  'counting_collection',
  'data_chart',
  'fraction_model',
  'percent_grid',
  'sandbox',
  'info_block',
  'canvas_breakdown',
  'subtitulo',
  'destacado',
  'figura',
  'glosario_tabla',
  'formula_destacada',
  'structural_breakdown',
  'source_analysis',
]);

const ExamSettingsSchema = z
  .object({
    shuffleBlocks: z.boolean().optional(),
    allowRejoin: z.boolean().optional(),
    integrityMode: z.string().optional(),
    advanceMode: z.string().optional(),
  })
  .passthrough();

const LlmBlockSchema = z
  .object({
    mode: z.string().trim().optional(),
    type: z.string().trim().optional(),
    label: z.string().optional(),
    title: z.string().optional(),
    payload: z.record(z.string(), z.any()).optional(),
  })
  .passthrough()
  .refine(
    (block) => Boolean(String(block.mode || '').trim() || String(block.type || '').trim()),
    { message: 'block requires mode or type' },
  );

export const SalaExamGenerationSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, 'title required')
    .max(64, 'title must be at most 64 characters')
    .refine((title) => title.length <= 64, {
      message: 'title exceeds maximum length of 64 characters',
    }),
  subjectTag: z.string().trim().optional(),
  resourceType: z.enum(['exam', 'guide']).optional(),
  isStudyGuide: z.boolean().optional(),
  settings: ExamSettingsSchema.optional(),
  curriculumObjectives: z
    .object({
      eje: z.string(),
      ejeLabel: z.string(),
      levelLabel: z.string().optional(),
      oaCode: z.string().optional(),
      oahCode: z.string().optional(),
      oaaCode: z.string().optional(),
      synthesizedGoal: z.string(),
      objetivo: z.string().optional(),
      habilidad: z.string().optional(),
      actitud: z.string().optional(),
      objetivos: z.array(z.string()).optional(),
      habilidades: z.array(z.string()).optional(),
    })
    .optional(),
  blocks: z.array(LlmBlockSchema).min(3, 'blocks must have at least 3 items'),
});

export type SalaExamGeneration = z.infer<typeof SalaExamGenerationSchema>;

export const GuideGenerationModeSchema = z.enum(['curricular', 'independent']);

export const GuideLengthSchema = z.enum(['short', 'medium', 'long']);

export const ExamExtensionSchema = z.enum(['ephemeral', 'short', 'medium', 'long']);

export const ExamFocusSchema = z.enum(['conceptual', 'learning', 'practical', 'inquiry']);

export const ResourceTypeSchema = z.enum(['exam', 'guide']);

export const DeliveryFormatSchema = z.enum(['print', 'interactive']);

export const DeliveryProfileSchema = z.enum(['learning_guide', 'interactive_class', 'assessment']);

export const EnfoqueDidacticoSchema = z.enum(['teorico', 'practico', 'aprendizaje', 'indagacion']);

export const GuideRecipeModeSchema = z.enum(['teorico', 'aprendizaje', 'practico', 'descubrimiento']);

export const ExamLevelSchema = z.enum([
  '1basico',
  '2basico',
  '3basico',
  '4basico',
  '5basico',
  '6basico',
  '7basico',
  '8basico',
]);

export const CurriculumTargetSchema = z.preprocess((value) => {
  if (!value || typeof value !== 'object') return undefined;
  const row = value as Record<string, unknown>;
  const axis = String(row.axis || row.eje || '').trim();
  const oaCode = String(row.oaCode || row.oa || '').trim();
  if (!axis || !oaCode) return undefined;
  const next: Record<string, unknown> = { axis, oaCode };
  const oah = String(row.oahCode || '').trim();
  const oaa = String(row.oaaCode || '').trim();
  if (oah) next.oahCode = oah;
  if (oaa) next.oaaCode = oaa;
  return next;
}, z
  .object({
    axis: z.string().trim().min(1, 'axis required'),
    oaCode: z.string().trim().min(1, 'oaCode required'),
    oahCode: z.string().trim().min(1).optional(),
    oaaCode: z.string().trim().min(1).optional(),
  })
  .optional());

export const VisualAnchorSchema = z.preprocess((value) => {
  if (!value || typeof value !== 'object') return undefined;
  const row = value as Record<string, unknown>;
  const imageUrl = String(row.imageUrl || '').trim();
  const wikiQuery = String(row.wikiQuery || '').trim();
  const note = String(row.note || '').trim();
  if (!imageUrl && !wikiQuery) return undefined;
  return {
    ...(imageUrl ? { imageUrl } : {}),
    ...(wikiQuery ? { wikiQuery } : {}),
    ...(note ? { note } : {}),
  };
}, z
  .object({
    imageUrl: z.string().url().optional(),
    wikiQuery: z.string().trim().min(2).max(200).optional(),
    note: z.string().trim().max(240).optional(),
  })
  .refine((value) => Boolean(value.imageUrl || value.wikiQuery), {
    message: 'visualAnchor requires imageUrl or wikiQuery',
  })
  .optional());

/** Contexto de sesión planificada — preview y generación desde ruta de aprendizaje. */
export const LinkedSessionContextSchema = z.preprocess((value) => {
  if (!value || typeof value !== 'object') return undefined;
  const row = { ...(value as Record<string, unknown>) };
  const sessionTitle = String(row.sessionTitle || '').trim();
  const sessionObjective = String(row.sessionObjective || '').trim();
  const subjectTag = String(row.subjectTag || '').trim();
  const hasLevel = row.level != null && String(row.level).trim() !== '';
  // Payload parcial (level/subjectTag o título) para heredar identidad sin IDs de curso.
  if (!sessionTitle && !sessionObjective && !subjectTag && !hasLevel) {
    return undefined;
  }
  row.courseId = String(row.courseId || '').trim() || '_';
  row.unitId = String(row.unitId || '').trim() || '_';
  row.sessionId = String(row.sessionId || '').trim() || '_';
  row.unitTitle = String(row.unitTitle || '').trim() || 'Unidad';
  row.sessionTitle = sessionTitle || sessionObjective || 'Sesión';
  row.sessionObjective = sessionObjective || sessionTitle || 'Sesión planificada';
  return row;
}, z.object({
  courseId: z.string().trim().min(1),
  unitId: z.string().trim().min(1),
  sessionId: z.string().trim().min(1),
  unitTitle: z.string().trim().min(1),
  sessionTitle: z.string().trim().min(1),
  sessionObjective: z.string().trim().min(1),
  sessionSummary: z.preprocess((value) => {
    const raw = String(value ?? '').trim();
    return raw || 'Sesión planificada';
  }, z.string().trim().min(1)),
  curriculumTarget: CurriculumTargetSchema.optional(),
  generationMode: GuideGenerationModeSchema.optional(),
  level: ExamLevelSchema.optional(),
  subjectTag: z.string().trim().min(1).max(48).optional(),
  curriculumSlice: z.preprocess((value) => {
    if (!value || typeof value !== 'object') return undefined;
    const row = value as Record<string, unknown>;
    const dim = String(row.dimensionDominante || '').trim();
    if (!['OA', 'OAH', 'OAA'].includes(dim)) return undefined;
    const next: Record<string, unknown> = { dimensionDominante: dim };
    const code = String(row.dimensionCode || '').trim();
    if (code) next.dimensionCode = code;
    return next;
  }, z
    .object({
      dimensionDominante: z.enum(['OA', 'OAH', 'OAA']),
      dimensionCode: z.string().trim().max(32).optional(),
    })
    .optional()),
  sessionConfig: z.preprocess((value) => {
    if (!value || typeof value !== 'object') return undefined;
    const row = value as Record<string, unknown>;
    const flowToArch: Record<string, string> = {
      scale_project: 'taller',
      measurement_inquiry: 'indagacion',
      source_inquiry: 'indagacion',
      workshop_scaffold: 'taller',
      skill_drill: 'destreza',
    };
    let archetype = String(row.archetype || '').trim();
    if (flowToArch[archetype]) archetype = flowToArch[archetype]!;
    if (!['destreza', 'indagacion', 'taller', 'evaluacion'].includes(archetype)) {
      return undefined;
    }
    const next: { archetype: string; variant?: string } = { archetype };
    if (
      archetype === 'destreza' &&
      (row.variant === 'guiado' || row.variant === 'autonomo')
    ) {
      next.variant = String(row.variant);
    }
    return next;
  }, z
    .object({
      archetype: z.enum(['destreza', 'indagacion', 'taller', 'evaluacion']),
      variant: z.enum(['guiado', 'autonomo']).optional(),
    })
    .optional()),
  sessionFlow: z.preprocess((value) => {
    if (!Array.isArray(value)) return undefined;
    const steps = value
      .filter((step) => step && typeof step === 'object')
      .map((step) => {
        const row = step as Record<string, unknown>;
        const title = String(row.title || '').trim();
        if (!title) return null;
        return { ...row, title };
      })
      .filter(Boolean);
    return steps.length ? steps : undefined;
  }, z
    .array(
      z.object({
        title: z.string().trim().min(1),
        durationMin: z.number().int().positive().optional(),
        durationLabel: z.string().trim().max(24).optional(),
        teacher: z.string().trim().max(4000).optional(),
        students: z.string().trim().max(4000).optional(),
        materials: z.string().trim().max(1000).optional(),
      }),
    )
    .max(12)
    .optional()),
  flowProfile: z
    .enum([
      'scale_project',
      'measurement_inquiry',
      'workshop_scaffold',
      'skill_drill',
      'source_inquiry',
      'generic',
    ])
    .optional(),
  allowMeasurementDomain: z.boolean().optional(),
  allowSourceInquiryDomain: z.boolean().optional(),
  visualAnchor: VisualAnchorSchema.optional(),
}).optional());

const GuideConfigSchema = z.object({
  includeTheory: z.boolean().optional(),
  includeActivities: z.boolean().optional(),
  guideLength: GuideLengthSchema.optional(),
});

const KnowledgeBaseInputSchema = z.object({
  text: z.string().trim().min(1, 'knowledgeBase.text required').max(120_000),
  fileName: z.string().trim().max(120).optional(),
  charCount: z.number().int().nonnegative().optional(),
  method: z.string().trim().max(64).optional(),
  truncated: z.boolean().optional(),
});

const MusicPrefsInputSchema = z
  .object({
    readingMethod: z.enum(['tradicional', 'takadimi', 'kodaly']).optional(),
    graphicMode: z.enum(['symbols', 'symbols-labels']).optional(),
  })
  .passthrough();

/** scoreContext se normaliza en el servidor — validación estructural mínima. */
const ScoreContextInputSchema = z
  .object({
    title: z.string().trim().max(200).optional(),
    summary: z.record(z.string(), z.unknown()).optional(),
    primaryPattern: z.array(z.string()).optional(),
    timeSignature: z.string().trim().max(16).optional(),
    bpm: z.number().optional(),
    llmBrief: z.string().trim().max(8000).optional(),
    melodyNotes: z
      .array(
        z.object({
          id: z.string(),
          pitch: z.string(),
          label: z.string().optional(),
          duration: z.string().optional(),
        }),
      )
      .optional(),
  })
  .passthrough();

function refineTeacherIntent(data: { contentContext?: string; prompt?: string }, ctx: z.RefinementCtx) {
  const intent = String(data.contentContext || data.prompt || '').trim();
  if (intent.length < 8) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'contentContext or prompt must be at least 8 characters',
      path: ['contentContext'],
    });
  }
}

function refineGuideCurriculumRules(
  data: {
    generationMode?: string;
    curriculumTarget?: z.infer<typeof CurriculumTargetSchema>;
    resourceType?: string;
    guideLength?: string;
    config?: { includeActivities?: boolean; guideLength?: string };
  },
  ctx: z.RefinementCtx,
) {
  if (data.generationMode === 'independent' && data.curriculumTarget) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'curriculumTarget is not allowed when generationMode is independent',
      path: ['curriculumTarget'],
    });
  }

  const includeActivities = data.config?.includeActivities !== false;
  if (data.resourceType === 'guide' && includeActivities) {
    const length = data.guideLength ?? data.config?.guideLength;
    if (length && !GuideLengthSchema.safeParse(length).success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'guideLength must be short, medium, or long',
        path: ['guideLength'],
      });
    }
  }
}

/** Contrato de entrada — POST /api/exams/generate (evaluación y guía). */
export const ExamGenerateRequestSchema = z
  .object({
    prompt: z.string().trim().max(4000).optional(),
    contentContext: z.string().trim().max(4000).optional(),
    teacherId: z.string().trim().max(64).optional(),
    subjectTag: z.string().trim().min(1).max(48).optional(),
    subjectExpertPrompt: z.string().trim().max(8000).optional(),
    extension: ExamExtensionSchema.default('short'),
    // level/subjectTag: sin default — el validator exige valor válido (body ∪ linkedSession).
    level: ExamLevelSchema.optional(),
    focus: ExamFocusSchema.default('practical'),
    enfoqueDidactico: EnfoqueDidacticoSchema.optional(),
    mode: GuideRecipeModeSchema.optional(),
    config: GuideConfigSchema.optional(),
    resourceType: ResourceTypeSchema.default('exam'),
    deliveryFormat: DeliveryFormatSchema.optional(),
    deliveryProfile: DeliveryProfileSchema.optional(),
    generationMode: GuideGenerationModeSchema.default('curricular'),
    guideLength: GuideLengthSchema.optional(),
    curriculumTarget: CurriculumTargetSchema.optional(),
    musicPrefs: MusicPrefsInputSchema.optional(),
    scoreContext: ScoreContextInputSchema.optional(),
    knowledgeBase: KnowledgeBaseInputSchema.optional(),
    courseProfile: CourseProfileSchema.optional(),
    /** Sesión planificada (hito) — se valida aquí para fail loud temprano. */
    linkedSessionContext: LinkedSessionContextSchema.optional(),
    /** Ancla visual docente — prioridad sobre Content Pack. */
    visualAnchor: VisualAnchorSchema.optional(),
  })
  .superRefine((data, ctx) => {
    refineTeacherIntent(data, ctx);
    refineGuideCurriculumRules(data, ctx);
  });

export type ExamGenerateRequestZod = z.infer<typeof ExamGenerateRequestSchema>;

/** Alias retrocompatible — guías usan el mismo contrato ampliado. */
export const GuideGenerationRequestSchema = ExamGenerateRequestSchema;

export type GuideGenerationRequestZod = ExamGenerateRequestZod;

/** Vista previa blueprint — subset del contrato de generación. */
export const GuideBlueprintPreviewRequestSchema = z
  .object({
    prompt: z.string().trim().max(4000).optional(),
    contentContext: z.string().trim().max(4000).optional(),
    topic: z.string().trim().max(4000).optional(),
    // Sin defaults fantasma: level/subjectTag los exige requireMaterialLevelAndSubject.
    level: ExamLevelSchema.optional(),
    subjectTag: z.string().trim().min(1).max(48).optional(),
    extension: ExamExtensionSchema.default('short'),
    generationMode: GuideGenerationModeSchema.default('curricular'),
    guideLength: GuideLengthSchema.optional(),
    curriculumTarget: CurriculumTargetSchema.optional(),
    linkedSessionContext: LinkedSessionContextSchema.optional(),
    config: GuideConfigSchema.optional(),
  })
  .superRefine((data, ctx) => {
    const intent = String(
      data.contentContext ||
        data.prompt ||
        data.topic ||
        data.linkedSessionContext?.sessionObjective ||
        '',
    ).trim();
    if (intent.length < 4) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'topic, contentContext or prompt required for blueprint preview',
        path: ['topic'],
      });
    }
    refineGuideCurriculumRules(
      {
        generationMode: data.generationMode,
        curriculumTarget: data.curriculumTarget,
        resourceType: 'guide',
        guideLength: data.guideLength,
        config: data.config,
      },
      ctx,
    );
  });

export function formatGenerateRequestValidationIssues(issues: z.ZodIssue[]): string {
  return issues
    .slice(0, 12)
    .map((issue) => {
      const path = issue.path?.length ? issue.path.join('.') : '(raíz)';
      return `- ${path}: ${issue.message}`;
    })
    .join('\n');
}

export type ParseGenerateRequestResult =
  | { success: true; data: ExamGenerateRequestZod }
  | { success: false; error: string; issues: z.ZodIssue[] };

/**
 * Valida el payload POST /api/exams/generate antes de invocar Mistral.
 */
export function parseExamGenerateRequest(raw: unknown): ParseGenerateRequestResult {
  const result = ExamGenerateRequestSchema.safeParse(raw);
  if (!result.success) {
    const first = result.error.issues[0];
    const path = first?.path?.length ? first.path.join('.') : '';
    const message = first?.message || 'exam_generate_request_invalid';
    return {
      success: false,
      error: path ? `${path}: ${message}` : message,
      issues: result.error.issues,
    };
  }
  return { success: true, data: result.data };
}

/**
 * Valida el payload POST /api/guides/blueprint/preview.
 */
export function parseGuideBlueprintPreviewRequest(raw: unknown): ParseGenerateRequestResult {
  const result = GuideBlueprintPreviewRequestSchema.safeParse(raw);
  if (!result.success) {
    const first = result.error.issues[0];
    const path = first?.path?.length ? first.path.join('.') : '';
    const message = first?.message || 'blueprint_preview_request_invalid';
    return {
      success: false,
      error: path ? `${path}: ${message}` : message,
      issues: result.error.issues,
    };
  }
  return { success: true, data: result.data as ExamGenerateRequestZod };
}

/**
 * Valida el payload de generación de guía antes de llamar al backend (cliente).
 */
export function parseGuideGenerationRequest(
  raw: unknown,
): { success: true; data: GuideGenerationRequestZod } | { success: false; error: string; issues?: z.ZodIssue[] } {
  const result = parseExamGenerateRequest(raw);
  if (!result.success) {
    return { success: false, error: result.error, issues: result.issues };
  }
  return { success: true, data: result.data };
}

export const MathEquationPayloadSchema = z.object({
  instruction: z.string().trim().min(1, 'instruction required'),
  equationLatex: z.string().trim().min(1, 'equationLatex required'),
  resolutionSteps: z.array(z.string().trim().min(1)).optional(),
});

export type MathEquationPayloadZod = z.infer<typeof MathEquationPayloadSchema>;

export const NumberLinePayloadSchema = z
  .object({
    instruction: z.string().trim().optional(),
    start: z.coerce.number().int().min(0),
    end: z.coerce.number().int(),
    step: z.coerce.number().int().positive(),
    missingNumbers: z.array(z.coerce.number().int()).min(1),
  })
  .refine((data) => data.end > data.start, {
    message: 'end must be greater than start',
    path: ['end'],
  });

export type NumberLinePayloadZod = z.infer<typeof NumberLinePayloadSchema>;

export const CountingItemTypeSchema = z.enum([
  'apple',
  'pencil',
  'star',
  'car',
  'ball',
  'flower',
  'block',
  'coin',
  'heart',
  'leaf',
]);

export const CountingCollectionGroupSchema = z.object({
  id: z.string().trim().min(1),
  itemType: CountingItemTypeSchema,
  count: z.coerce.number().int().min(0).max(20),
  label: z.string().trim().optional(),
});

export const CountingCollectionPayloadSchema = z.object({
  instruction: z.string().trim().min(1, 'instruction required'),
  step: z.coerce.number().int().positive().max(20).optional(),
  groups: z.array(CountingCollectionGroupSchema).min(1).max(6),
});

export type CountingCollectionPayloadZod = z.infer<typeof CountingCollectionPayloadSchema>;

export const DataChartTypeSchema = z.enum(['bar', 'pie', 'line', 'dot', 'table']);

export const DataChartPointSchema = z.object({
  label: z.string().describe('Nombre de la categoría o punto de datos'),
  value: z.number().describe('Valor numérico exacto'),
});

export const DataChartPayloadSchema = z.object({
  chartType: DataChartTypeSchema,
  title: z.string().describe('Título descriptivo del gráfico o tabla'),
  instruction: z
    .string()
    .optional()
    .describe('Consigna breve distinta del título — tablas de medición OA 20'),
  blueprintPurpose: z
    .string()
    .optional()
    .describe('Propósito pedagógico del slot — sobrevive validateExam para impresión'),
  xAxisLabel: z.string().optional().describe("Etiqueta del eje X (ej: 'Categorías', 'Meses')"),
  yAxisLabel: z.string().optional().describe("Etiqueta del eje Y (ej: 'Cantidad', 'Frecuencia')"),
  surveyChartTemplate: z
    .boolean()
    .optional()
    .describe('Plantilla vacía para que el estudiante complete el gráfico de encuesta'),
  data: z
    .array(DataChartPointSchema)
    .min(2)
    .max(10)
    .describe('Arreglo de datos a graficar'),
});

export type DataChartPayloadZod = z.infer<typeof DataChartPayloadSchema>;

export const FractionVisualTypeSchema = z.enum(['circle', 'bar']);

export const FractionModelPayloadSchema = z
  .object({
    instruction: z.string().describe('Consigna breve sobre la fracción ilustrada'),
    numerator: z.coerce.number().int().min(0).describe('Partes coloreadas del total'),
    denominator: z.coerce
      .number()
      .int()
      .positive()
      .max(12)
      .describe('Total de partes iguales — límite cognitivo básico'),
    visualType: FractionVisualTypeSchema.describe('Si se dibuja como pizza o como barra'),
  })
  .superRefine((data, ctx) => {
    if (data.numerator > data.denominator) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'numerator must not exceed denominator',
        path: ['numerator'],
      });
    }
  });

export type FractionModelPayloadZod = z.infer<typeof FractionModelPayloadSchema>;

export const PercentGridPayloadSchema = z.object({
  instruction: z.string().describe('Consigna breve sobre el porcentaje ilustrado'),
  percentage: z.coerce
    .number()
    .min(0)
    .max(100)
    .describe('Porcentaje exacto a representar en la grilla 10×10'),
});

export type PercentGridPayloadZod = z.infer<typeof PercentGridPayloadSchema>;

export const CartesianQuadrantsSchema = z.enum(['1', '4']);

export const CartesianPointSchema = z.object({
  x: z.coerce.number(),
  y: z.coerce.number(),
  label: z.string().optional(),
});

export const CartesianPolygonVertexSchema = z.object({
  x: z.coerce.number(),
  y: z.coerce.number(),
});

export const CartesianPolygonSchema = z.object({
  vertices: z.array(CartesianPolygonVertexSchema).min(3),
  color: z.string().optional(),
});

export const CartesianPlanePayloadSchema = z.object({
  instruction: z.string().describe('Consigna sobre ubicación, transformación o figura en el plano'),
  quadrants: CartesianQuadrantsSchema.describe(
    '1 para plano positivo (básica), 4 para plano completo (media)',
  ),
  gridSize: z.coerce.number().positive().max(20).default(10).describe(
    'Límite de los ejes X e Y (ej: 10 significa de -10 a 10 en cuadrante 4, o 0 a 10 en cuadrante 1)',
  ),
  points: z.array(CartesianPointSchema).optional(),
  polygons: z
    .array(CartesianPolygonSchema)
    .optional()
    .describe('Figuras, traslaciones o reflexiones conectando vértices'),
});

export type CartesianPlanePayloadZod = z.infer<typeof CartesianPlanePayloadSchema>;

export const GeometryAnnotationSchema = z.object({
  label: z.string().trim().min(1),
  kind: z.enum(['vertex', 'angle']).optional(),
  at: z.string().trim().optional(),
});

export const GeometryDimensionsSchema = z
  .object({
    width: z.coerce.number().positive().optional(),
    height: z.coerce.number().positive().optional(),
    radius: z.coerce.number().positive().optional(),
    base: z.coerce.number().positive().optional(),
  })
  .passthrough();

export const TriangleSubtypeSchema = z.enum(['right', 'isosceles', 'equilateral', 'scalene']);

export const GeometryPayloadSchema = z
  .object({
    shapeType: z.enum(['rectangle', 'triangle', 'circle', 'compound']),
    dimensions: GeometryDimensionsSchema,
    labels: z
      .object({
        top: z.string().optional(),
        right: z.string().optional(),
        bottom: z.string().optional(),
        left: z.string().optional(),
        center: z.string().optional(),
      })
      .optional()
      .default({}),
    triangleSubtype: TriangleSubtypeSchema.optional(),
    annotations: z.array(GeometryAnnotationSchema).optional(),
    highlightEdges: z
      .array(z.enum(['top', 'right', 'bottom', 'left']))
      .optional(),
    parts: z.array(z.record(z.string(), z.any())).optional(),
    unit: z.string().optional(),
  })
  .superRefine((payload, ctx) => {
    if (payload.shapeType !== 'triangle' && payload.triangleSubtype != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'triangleSubtype is only allowed when shapeType is triangle',
        path: ['triangleSubtype'],
      });
    }
  });

export const GeometryCanvasPayloadSchema = z.object({
  title: z.string().trim().min(1),
  bodyText: z.string().optional(),
  sectionTitle: z.string().optional(),
  prompt: z.string().optional(),
  geometry: GeometryPayloadSchema,
  formulaHint: z.string().optional(),
  teacherScript: z.string().optional(),
});

export type GeometryCanvasPayloadZod = z.infer<typeof GeometryCanvasPayloadSchema>;

export interface ParseLlmExamResult {
  success: true;
  data: SalaExamGeneration;
}

export interface ParseLlmExamError {
  success: false;
  error: string;
  issues?: z.ZodIssue[];
}

/**
 * Extrae y valida el JSON raíz de una respuesta Mistral.
 */
export function parseAndValidateLlmExam(
  raw: unknown,
  context: LlmExamSanitizeContext = {},
): ParseLlmExamResult | ParseLlmExamError {
  let parsed: unknown = raw;

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    const fenced = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/i);
    const candidate = fenced ? fenced[1]!.trim() : trimmed;
    try {
      parsed = JSON.parse(candidate);
    } catch {
      return { success: false, error: 'invalid_json' };
    }
  }

  const preprocessed = sanitizeLlmExamRaw(parsed, context);
  const result = SalaExamGenerationSchema.safeParse(preprocessed);
  if (!result.success) {
    const first = result.error.issues[0];
    return {
      success: false,
      error: first?.message || 'schema_validation_failed',
      issues: result.error.issues,
    };
  }

  return { success: true, data: result.data };
}

/**
 * Valida que los modos de bloque estén en la lista permitida para esta generación.
 */
export function validateAllowedModes(
  exam: SalaExamGeneration,
  allowedModes: string[],
): { ok: true } | { ok: false; error: string } {
  const allowed = new Set(allowedModes.map((m) => m.toLowerCase()));
  for (const [index, block] of exam.blocks.entries()) {
    const mode = String(block.mode || block.type || '').toLowerCase();
    if (!mode) {
      return { ok: false, error: `block[${index}] missing mode` };
    }
    if (!allowed.has(mode) && !isPedagogicalAiType(mode)) {
      return { ok: false, error: `block[${index}] mode "${mode}" not allowed` };
    }
  }
  return { ok: true };
}

function isPedagogicalAiType(type: string): boolean {
  return [
    'content_reading',
    'definition_glossary',
    'atlas_context',
    'section_header',
    'highlight_box',
    'image_placeholder',
    'glossary_table',
    'math_formula',
    'quiz_multiple',
    'quiz_true_false',
    'fill_blank',
    'matching_pairs',
    'ordering_steps',
    'classification_groups',
    'open_case',
    'research_prompt',
    'inquiry_scenario',
  ].includes(type);
}

/**
 * Rechaza formula_destacada en humanidades en el contrato post-parse.
 */
export function rejectHumanitiesFormulaBlocks(
  exam: SalaExamGeneration,
  subjectTag?: string,
): { ok: true } | { ok: false; error: string } {
  const tag = String(subjectTag || exam.subjectTag || '').toLowerCase();
  if (tag !== 'historia' && tag !== 'lenguaje') return { ok: true };

  const hasFormula = exam.blocks.some((b) => {
    const mode = String(b.mode || '').toLowerCase();
    const type = String(b.type || '').toLowerCase();
    return (
      mode === 'formula_destacada' ||
      mode === 'math_equation' ||
      type === 'math_formula'
    );
  });
  if (hasFormula) {
    return {
      ok: false,
      error:
        'formula_destacada/math_equation/math_formula is not allowed for historia/lenguaje — use destacado or info_block',
    };
  }
  return { ok: true };
}
